import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AgentHistoryFilterBar } from './AgentHistoryFilterBar';
import { AgentHistoryDetail } from './AgentHistoryDetail';
import { GovernanceHistoryTab } from './GovernanceHistoryTab';
import { SystemLogTable } from '../SystemLog/SystemLogTable';
import { useAckEventMutation } from '../SystemLog/useSystemLog';
import { Pager } from '../../components/Pager';
import {
  useAgentHistoryDetail,
  useAgentHistoryList,
  useTraceChain,
} from './useAgentHistory';
import {
  DEFAULT_AGENT_HISTORY_FILTERS,
  filtersFromSearchParams,
  filtersToSearchParams,
  type AgentHistoryFilters,
} from './agentHistory.types';
import { useAuthStore } from '../../stores/authStore';
import '../SystemLog/systemLog.css';
import './agentHistory.css';

// 권위: docs/ARCH_DASHBOARD.md §4.4 화면 4 Agent History.
// - 필터 상태 ↔ URL 쿼리스트링 동기화 (북마크/공유 가능).
// - `/history/:eventId` 딥링크 → 상세 모달 자동 오픈. 닫으면 `/history` 로 복귀(현 필터 유지).
// - SystemLogTable 재사용 (컬럼·색상 규약 동일).
// - 상세 모달은 "상세" / "연관 이벤트 체인" 2탭.
// - 백테스트 이력 탭: **데이터 없음**. 상단 탭에서 "추후 활성화" placeholder 로만 노출.

// 탭 6-D `governance-history` 는 Override 배너에서 딥링크로 접근
// (/history?tab=governance-history). 그래서 URL 쿼리를 단일 진실원으로 사용한다.
type TopTab = 'events' | 'backtest' | 'governance-history';

const VALID_TABS: readonly TopTab[] = ['events', 'backtest', 'governance-history'];

function parseTab(sp: URLSearchParams): TopTab {
  const raw = sp.get('tab');
  return (VALID_TABS as readonly string[]).includes(raw ?? '')
    ? (raw as TopTab)
    : 'events';
}

export default function AgentHistoryPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // URL 쿼리 `?tab=` 로부터 파생 — useState 대신 derived value.
  // 외부 링크(override banner 의 /history?tab=governance-history) 가 바로 반영된다.
  const tab: TopTab = parseTab(searchParams);
  const setTab = useCallback(
    (next: TopTab) => {
      const sp = new URLSearchParams(searchParams);
      if (next === 'events') sp.delete('tab');
      else sp.set('tab', next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const filters = useMemo<AgentHistoryFilters>(
    () => filtersFromSearchParams(searchParams),
    [searchParams]
  );

  const setFilters = useCallback(
    (next: AgentHistoryFilters) => {
      setSearchParams(filtersToSearchParams(next), { replace: true });
    },
    [setSearchParams]
  );

  const onReset = useCallback(() => {
    setSearchParams(filtersToSearchParams(DEFAULT_AGENT_HISTORY_FILTERS), { replace: true });
  }, [setSearchParams]);

  const list = useAgentHistoryList(filters);
  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  // ── 딥링크 상세 모달 ─────────────────────────────
  const detail = useAgentHistoryDetail(eventId ?? null);
  // 체인은 detail fetch 완료 후 traceId 로 조회 (없으면 disabled).
  const chain = useTraceChain(detail.data?.traceId ?? null);
  const ack = useAckEventMutation(user?.email ?? 'unknown');

  const closeDetail = useCallback(() => {
    navigate(
      {
        pathname: '/history',
        search: searchParams.toString() ? `?${searchParams.toString()}` : '',
      },
      { replace: false }
    );
  }, [navigate, searchParams]);

  useEffect(() => {
    if (!eventId) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') closeDetail();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [eventId, closeDetail]);

  return (
    <div className="syslog-page">
      <header className="syslog-page__header">
        <h1 className="syslog-page__title">에이전트 실행 이력</h1>
        <div className="syslog-page__meta">
          총 {total.toLocaleString('ko-KR')} 건
          {list.data?.unackedCount !== undefined && list.data.unackedCount > 0 && (
            <> · 미확인 {list.data.unackedCount.toLocaleString('ko-KR')}</>
          )}
          {list.data?.filteredOutCount !== undefined && list.data.filteredOutCount > 0 && (
            <> · (클라이언트 필터로 {list.data.filteredOutCount}건 숨김)</>
          )}
          {list.isFetching && <span className="syslog-muted"> · 갱신 중…</span>}
        </div>
      </header>

      <nav className="agent-hist-tabs agent-hist-tabs--top" aria-label="Agent History tabs">
        <button
          type="button"
          className={`agent-hist-tabs__tab${tab === 'events' ? ' agent-hist-tabs__tab--active' : ''}`}
          onClick={() => setTab('events')}
        >
          이벤트 이력
        </button>
        <button
          type="button"
          className={`agent-hist-tabs__tab${tab === 'backtest' ? ' agent-hist-tabs__tab--active' : ''}`}
          onClick={() => setTab('backtest')}
        >
          백테스트 이력
        </button>
        <button
          type="button"
          className={`agent-hist-tabs__tab${tab === 'governance-history' ? ' agent-hist-tabs__tab--active' : ''}`}
          onClick={() => setTab('governance-history')}
        >
          GovernanceContext 이력
        </button>
      </nav>

      {tab === 'events' && (
        <>
          <AgentHistoryFilterBar filters={filters} onChange={setFilters} onReset={onReset} />

          {list.isError && (
            <div className="syslog-warning-banner">
              목록 로드 실패: {list.error?.message ?? 'unknown'}
            </div>
          )}

          <SystemLogTable
            items={items}
            isLoading={list.isLoading}
            realtimeCount={0}
            onRowClick={(it) => {
              navigate({
                pathname: `/history/${it.eventId}`,
                search: searchParams.toString() ? `?${searchParams.toString()}` : '',
              });
            }}
          />

          <Pager
            page={filters.page}
            pageSize={filters.pageSize}
            total={total}
            onPageChange={(p) => setFilters({ ...filters, page: p })}
          />
        </>
      )}

      {tab === 'backtest' && (
        <div className="agent-hist-placeholder">
          <h2>백테스트 이력</h2>
          <p>
            백테스트 실행 인프라(QA 에이전트 · <code>tests/</code>) 가 아직 프로덕션 데이터를 생산하지
            않습니다. 백테스트 엔진이 활성화되면 본 탭에서 CAGR / 샤프 / MDD / 승률 / 파라미터 변경
            추이를 조회할 수 있습니다.
          </p>
          <p className="syslog-muted">추후 활성화 (docs/ARCH_DASHBOARD.md §4.4 참조).</p>
        </div>
      )}

      {tab === 'governance-history' && <GovernanceHistoryTab />}

      {eventId && (
        <AgentHistoryDetail
          detail={detail.data ?? null}
          isLoading={detail.isLoading}
          isError={detail.isError}
          error={(detail.error as Error | null) ?? null}
          ackBusy={ack.isPending}
          onClose={closeDetail}
          onAck={(note) => {
            ack.mutate({ eventId, note: note || undefined });
          }}
          chain={chain.data ?? []}
          chainLoading={chain.isLoading}
        />
      )}
    </div>
  );
}
